import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import Reveal from "@/components/7fcc/Reveal";
import SectionHeader from "@/components/7fcc/SectionHeader";
import Logo from "@/components/7fcc/Logo";
import VslPlayer from "@/components/7fcc/VslPlayer";
import GhlSurvey from "@/components/7fcc/GhlSurvey";
import FAQAccordion from "@/components/7fcc/FAQAccordion";
import CTAButton from "@/components/7fcc/CTAButton";
import VideoTestimonials from "@/components/7fcc/VideoTestimonials";
import {
  BRAND,
  HERO,
  VSL,
  OPPORTUNITY,
  PROOF,
  VIDEO_TESTIMONIALS_CAPTION,
  TESTIMONIAL_VIDEOS,
  WINS_VIDEOS,
  QUIZ,
  SURVEY,
  FAQ,
  FOOTER,
} from "@/data/startContent";

const pick = (keys) => keys.map((key) => TESTIMONIAL_VIDEOS[key]);
import { EASE } from "@/lib/motion";

// ═══════════════════════════════════════════════════════════════════════
// /START campaign sections — exact mirrors of /ready, fed from
// src/data/startContent.js. Visual components are shared; content is
// fully isolated. All testids are prefixed "start-".
// ═══════════════════════════════════════════════════════════════════════

export function StartHero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 18 });
  const sy = useSpring(my, { stiffness: 50, damping: 18 });
  const logoX = useTransform(sx, [-0.5, 0.5], [-8, 8]);
  const logoY = useTransform(sy, [-0.5, 0.5], [-5, 5]);

  const onMouseMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative flex flex-col items-center overflow-hidden bg-ink-950 px-6 pb-14 pt-12 text-center md:pb-16 md:pt-14"
      data-testid="start-hero-section"
    >
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-[2%] h-[42vh] w-[70vw] max-w-[880px] -translate-x-1/2 bg-[radial-gradient(ellipse,rgba(216,27,96,0.18),transparent_65%)] blur-3xl"
      />

      <motion.div
        style={{ x: logoX, y: logoY }}
        initial={{ opacity: 0, scale: 0.96, filter: "blur(10px)" }}
        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        transition={{ duration: 1.1, ease: EASE }}
        className="relative w-[min(420px,70vw)] mix-blend-screen"
      >
        <Logo halo priority className="w-full" />
      </motion.div>

      <div className="relative mt-5 flex max-w-5xl flex-col items-center">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
          className="mb-5 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-light"
          data-testid="start-hero-eyebrow"
        >
          <span className="h-px w-8 bg-brand/60" aria-hidden="true" />
          {HERO.eyebrow}
          <span className="h-px w-8 bg-brand/60" aria-hidden="true" />
        </motion.p>

        <h1 className="font-display text-[clamp(1.75rem,3.6vw,2.75rem)] font-semibold uppercase leading-[1.1] tracking-[-0.01em]">
          {HERO.headlineLines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="text-chrome block"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.45 + i * 0.13, ease: EASE }}
                data-testid={`start-hero-headline-${i + 1}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
      </div>
    </section>
  );
}

export function StartVslSection() {
  return (
    <section id="vsl" className="relative bg-ink-900 px-6 py-16 md:py-24" data-testid="start-vsl-section">
      <div className="mx-auto max-w-[900px]">
        <SectionHeader center eyebrow={VSL.eyebrow} title={VSL.headline} support={VSL.support} testId="start-vsl-heading" />
        <Reveal delay={0.15} className="mt-10">
          <VslPlayer src={VSL.videoSrc} label={VSL.videoLabel} testId="start-vsl-video" />
        </Reveal>
        <Reveal delay={0.22}>
          <p className="mt-4 text-center text-sm text-fg-dim" data-testid="start-vsl-caption">
            {VSL.caption}
          </p>
        </Reveal>      </div>
    </section>
  );
}

export function StartQuizSection() {
  return (
    <section id="qualify" className="relative bg-ink-950 px-6 py-16 md:py-24" data-testid="start-quiz-section">
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 h-[560px] w-[860px] max-w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(216,27,96,0.07),transparent_65%)] blur-3xl"
      />
      <div className="relative mx-auto max-w-3xl">        <div className="relative rounded-2xl border border-glowblue bg-ink-850/80 p-6 shadow-panel backdrop-blur-sm md:p-10 md:pb-8">
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[rgba(216,27,96,0.6)] to-transparent"
            aria-hidden="true"
          />
          <SectionHeader
            center
            eyebrow={QUIZ.eyebrow}
            title={QUIZ.headline}
            support={QUIZ.support}
            testId="start-quiz-heading"
          />
          <Reveal delay={0.15} className="mt-4 md:mt-6">
            <GhlSurvey
              src={SURVEY.src}
              iframeId={SURVEY.iframeId}
              title={SURVEY.title}
              testId="start-ghl-survey"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function StartOpportunitySection() {
  return (
    <section className="bg-ink-900 px-6 py-16 md:py-24" data-testid="start-opportunity-section">
      <div className="mx-auto max-w-5xl">
        <SectionHeader
          center
          eyebrow={OPPORTUNITY.eyebrow}
          title={OPPORTUNITY.headline}
          support={OPPORTUNITY.intro}
          testId="start-opportunity-heading"
        />

        <div className="mt-10 grid gap-5 md:grid-cols-3 md:mt-12">
          {OPPORTUNITY.cards.map((card, i) => (
            <Reveal key={card.title} delay={i * 0.08} className="h-full">
              <div
                className="h-full rounded-lg border border-line bg-ink-950/60 p-6"
                data-testid={`start-opportunity-card-${i + 1}`}
              >
                <h3 className="font-display text-lg font-medium text-fg">{card.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-fg-muted">{card.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-5 grid gap-5 md:grid-cols-3">
          {OPPORTUNITY.steps.map((step, i) => (
            <Reveal key={step.title} delay={i * 0.08} className="h-full">
              <div
                className="flex h-full items-start gap-4 rounded-lg border border-line bg-ink-950/60 p-6"
                data-testid={`start-opportunity-step-${i + 1}`}
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-bold text-white"
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-display text-base font-medium text-fg">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-fg-muted">{step.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function StartProofSection() {
  return (
    <section className="bg-ink-950 px-6 py-16 md:py-24" data-testid="start-proof-section">
      <div className="mx-auto max-w-5xl">
        <SectionHeader
          center
          title={PROOF.headline}
          support={PROOF.support}
          testId="start-proof-heading"
        />

        <div
          className="mt-10 gap-5 [column-fill:_balance] sm:columns-2 md:columns-3 md:mt-12"
          data-testid="start-proof-wall"
        >
          {PROOF.items.map((item, i) => (
            <Reveal key={item.src} delay={i * 0.06} className="mb-5 break-inside-avoid">
              <figure
                className="overflow-hidden rounded-lg border border-line bg-ink-900"
                data-testid={`start-proof-item-${i + 1}`}
              >
                <img src={item.src} alt={item.alt} loading="lazy" className="w-full" />
              </figure>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15} className="mt-12 md:mt-16">
          <div
            className="mx-auto grid max-w-4xl items-center gap-8 rounded-lg border border-line bg-ink-900/60 p-6 md:grid-cols-[1.05fr_1fr] md:p-8"
            data-testid="start-featured-testimonial"
          >
            <figure
              className="overflow-hidden rounded-lg border border-line bg-ink-900"
              data-testid="start-featured-media"
            >
              <img src={PROOF.featured.src} alt={PROOF.featured.alt} loading="lazy" className="w-full" />
            </figure>
            <div className="text-center md:text-left">
              <h3 className="font-display text-xl font-medium text-fg md:text-2xl" data-testid="start-featured-name">
                {PROOF.featured.name}
              </h3>
              <p
                className="mt-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-light"
                data-testid="start-featured-result"
              >
                {PROOF.featured.result}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-fg-muted md:text-base" data-testid="start-featured-text">
                {PROOF.featured.text}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function StartVideoWinsSection() {
  return (
    <section className="bg-ink-900 px-6 py-16 md:py-24" data-testid="start-video-wins-section">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <p
            className="flex items-center justify-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-light"
            data-testid="start-video-wins-eyebrow"
          >
            <span className="h-px w-6 bg-brand/60" aria-hidden="true" />
            Wins In Their Own Words
            <span className="h-px w-6 bg-brand/60" aria-hidden="true" />
          </p>
        </Reveal>

        <div className="mt-8 md:mt-10">
          <VideoTestimonials videos={pick(WINS_VIDEOS)} testIdPrefix="start-video-testimonial" />
        </div>

        <Reveal delay={0.12}>
          <p
            className="mt-8 text-center text-sm leading-relaxed text-fg-muted md:mt-10"
            data-testid="start-video-wins-caption"
          >
            {VIDEO_TESTIMONIALS_CAPTION}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function StartFaqSection() {
  return (
    <section className="bg-ink-950 px-6 py-16 md:py-24" data-testid="start-faq-section">
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <h2
            className="text-center font-display text-3xl font-semibold leading-[1.08] text-fg md:text-5xl"
            data-testid="start-faq-heading"
          >
            {FAQ.heading}
          </h2>
        </Reveal>
        <Reveal delay={0.12} className="mt-12">
          <FAQAccordion items={FAQ.items} />
        </Reveal>
      </div>
    </section>
  );
}

export function StartBottomCta() {
  return (
    <section className="bg-ink-950 px-6 py-14 text-center md:py-16" data-testid="start-bottom-cta">
      <Reveal>
        <CTAButton label="See If You Qualify Here" targetId="qualify" testId="start-bottom-cta" />
      </Reveal>
    </section>
  );
}

export function StartFooter() {
  return (
    <footer className="border-t border-line bg-ink-950 px-6 py-14" data-testid="start-footer">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 text-center">
        <Logo className="w-44 opacity-90" />
        <p className="font-display text-sm uppercase tracking-[0.28em] text-silver" data-testid="start-footer-brand">
          {BRAND.name}
        </p>
        <nav className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3" aria-label="Legal">
          {FOOTER.links.map((link) => (
            <a
              key={link.label}
              href={link.href}
              data-testid={`start-footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-xs uppercase tracking-[0.2em] text-fg-dim transition-colors duration-300 hover:text-fg"
            >
              {link.label}
            </a>
          ))}
        </nav>
        <p className="text-xs text-fg-dim" data-testid="start-footer-copyright">
          {FOOTER.copyright}
        </p>
      </div>
    </footer>
  );
}
